// adapters/web/callUI — the SDK's native, glassmorphism call UI for the browser (and, since
// it's plain DOM, inside a WebView for web-to-native). It renders the familiar WhatsApp
// (Secure 1:1) or Google Meet (mesh room / managed conference) experience from the pure
// `callUxSpec` in zr-core and wires every control to the ZrClient the app already created.
//
//   import { mountCallUI } from '@zr/adapter-web';
//   const ui = mountCallUI(client, { style: 'auto', glass: true });   // auto: 1:1→WhatsApp, room→Meet
//   // ...later
//   ui.unmount();
//
// No import-time DOM access (safe to import in Node/tests); everything touches `document`
// only inside mountCallUI so the module itself stays portable.

import {
  callUxSpec,
  formatCallDuration,
  REACTION_EMOJIS,
  type CallUxChoice,
  type CallControlSpec,
} from 'zr-webrtc';

/** The subset of ZrClient the UI drives — kept structural to avoid a dep cycle on zr-call. */
export interface CallUIClient {
  events: { on(h: (e: any) => void): () => void };
  localStream?: MediaStream;
  displayStream?: MediaStream;
  callPhase?: string;
  incomingCall?: { from: string; displayName?: string };
  e2eeActive?: boolean;
  raisedHands?: { peerId: string; at: number }[];
  isScreenSharing?: boolean;
  getParticipantStream?(id: string): MediaStream | undefined;
  setMicEnabled(on: boolean): void;
  setCameraEnabled(on: boolean): void;
  startScreenShare?(opts?: any): Promise<void>;
  stopScreenShare?(): Promise<void>;
  raiseHand?(on: boolean): void;
  sendReaction?(emoji: string): void;
  hangup?(): Promise<void> | void;
  leave?(): Promise<void> | void;
  accept?(opts?: any): Promise<void> | void;
  decline?(opts?: any): Promise<void> | void;
}

export interface CallUIOptions {
  /** 'whatsapp' | 'google-meet' | 'auto' (default). Auto: 1:1 call → WhatsApp, room → Meet. */
  style?: CallUxChoice;
  mode?: 'call' | 'group' | 'broadcast';
  maxParticipants?: number;
  /** Apply the frosted-glass surface treatment (default true). */
  glass?: boolean;
  /** Where to mount (default document.body). */
  mount?: HTMLElement;
  /** Name shown for the remote peer / room in the header. */
  displayName?: string;
}

export interface CallUIHandle {
  root: HTMLElement;
  unmount(): void;
  /** Re-render under a different style at runtime (e.g. a demo toggle). */
  setStyle(style: CallUxChoice): void;
}

const STYLE_ID = 'zr-call-ui-styles';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, any> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else if (k === 'style') (node as HTMLElement).style.cssText = v;
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
    else if (v != null) node.setAttribute(k, String(v));
  }
  for (const c of children) node.append(c);
  return node;
}

/** Inject the glassmorphism stylesheet once per document. */
function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const css = `
  .zr-call-ui { position: fixed; inset: 0; z-index: 2147483000; display: flex; flex-direction: column;
    font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #f8fafc;
    background: radial-gradient(circle at top right, rgba(153,87,210,.18), transparent 30%),
                radial-gradient(circle at bottom left, rgba(56,189,248,.16), transparent 30%),
                linear-gradient(160deg, #0b1220, #05070f); }
  .zr-call-ui * { box-sizing: border-box; }
  .zr-glass { background: rgba(15,23,42,.55); border: 1px solid rgba(255,255,255,.12);
    backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); box-shadow: 0 18px 40px rgba(0,0,0,.35); }
  .zr-cu-header { display:flex; align-items:center; justify-content:space-between; gap:12px;
    padding: 14px 18px; margin: 12px; border-radius: 18px; }
  .zr-cu-title { display:flex; flex-direction:column; gap:2px; }
  .zr-cu-title b { font-size: 16px; font-weight: 700; }
  .zr-cu-sub { font-size: 12px; color:#cbd5e1; display:flex; gap:8px; align-items:center; }
  .zr-cu-badge { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:700;
    padding: 4px 10px; border-radius: 999px; }
  .zr-cu-e2ee { color:#4ade80; background: rgba(74,222,128,.12); border:1px solid rgba(74,222,128,.3); }
  .zr-cu-count { color:#93c5fd; background: rgba(147,197,253,.12); border:1px solid rgba(147,197,253,.3); }
  .zr-cu-stage { flex:1; position:relative; min-height:0; margin: 0 12px; }
  /* WhatsApp: fullscreen remote + self PiP */
  .zr-cu-stage.fullscreen-pip .zr-cu-remote { position:absolute; inset:0; border-radius:20px; overflow:hidden; }
  .zr-cu-stage.fullscreen-pip .zr-cu-remote video { width:100%; height:100%; object-fit:cover; background:#02070d; }
  .zr-cu-stage.fullscreen-pip .zr-cu-self { position:absolute; right:16px; bottom:16px; width:118px; height:172px;
    border-radius:16px; overflow:hidden; border:2px solid rgba(255,255,255,.25); z-index:3; }
  .zr-cu-stage.fullscreen-pip .zr-cu-self video { width:100%; height:100%; object-fit:cover; background:#02070d; }
  /* Meet: responsive grid */
  .zr-cu-stage.grid .zr-cu-grid { position:absolute; inset:0; display:grid; gap:10px;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); align-content:center; overflow:auto; padding:6px; }
  .zr-cu-tile { position:relative; border-radius:16px; overflow:hidden; min-height:150px;
    background: linear-gradient(180deg, rgba(16,25,36,.85), rgba(3,10,17,.96)); border:1px solid rgba(255,255,255,.08); }
  .zr-cu-tile.speaking { border-color: rgba(84,191,212,.7); box-shadow: 0 0 0 1px rgba(84,191,212,.4); }
  .zr-cu-tile video { width:100%; height:100%; object-fit:cover; background:#02070d; }
  .zr-cu-tile .zr-cu-name { position:absolute; left:10px; bottom:10px; padding:5px 9px; border-radius:10px;
    background: rgba(0,0,0,.45); font-size:12px; font-weight:600; }
  .zr-cu-tile .zr-cu-hand { position:absolute; top:10px; right:10px; font-size:18px; display:none; }
  .zr-cu-tile.raised .zr-cu-hand { display:block; }
  /* Control bar */
  .zr-cu-bar { display:flex; align-items:center; justify-content:center; gap:10px; flex-wrap:wrap;
    padding: 12px; margin: 12px; border-radius: 999px; }
  .zr-cu-btn { min-width:56px; min-height:56px; padding: 0 14px; border-radius:999px; border:1px solid rgba(255,255,255,.14);
    background: rgba(255,255,255,.08); color:#f3f5f7; font-weight:700; font-size:13px; cursor:pointer;
    display:inline-flex; flex-direction:column; align-items:center; justify-content:center; gap:2px;
    transition: transform .12s ease, background .12s ease, border-color .12s ease; }
  .zr-cu-btn .zr-ic { font-size:20px; line-height:1; }
  .zr-cu-btn .zr-lb { font-size:10px; opacity:.85; }
  .zr-cu-btn:hover { transform: translateY(-1px); background: rgba(255,255,255,.14); }
  .zr-cu-btn.off { background: rgba(239,68,68,.18); border-color: rgba(239,68,68,.4); color:#ffd4cf; }
  .zr-cu-btn.on-accent { background: rgba(153,87,210,.24); border-color: rgba(153,87,210,.5); color:#e9d5ff; }
  .zr-cu-btn.danger { background: linear-gradient(135deg,#d25147,#b3261e); border-color:transparent; color:#fff; }
  .zr-cu-btn.accept { background: linear-gradient(135deg,#2b9a63,#157347); border-color:transparent; color:#fff; }
  /* Incoming overlay */
  .zr-cu-incoming { position:absolute; inset:0; z-index:20; display:flex; flex-direction:column; align-items:center;
    justify-content:center; gap:26px; text-align:center; border-radius:20px; }
  .zr-cu-avatar { width:104px; height:104px; border-radius:999px; display:grid; place-items:center; font-size:38px; font-weight:800;
    background: linear-gradient(135deg,#9957D2,#38bdf8); }
  .zr-cu-incoming h2 { margin:0; font-size:26px; }
  .zr-cu-incoming p { margin:0; color:#cbd5e1; }
  .zr-cu-incoming .zr-cu-bar { background:transparent; box-shadow:none; border:0; }
  /* Reaction tray + float */
  .zr-cu-tray { position:absolute; bottom:96px; left:50%; transform:translateX(-50%); display:none; gap:8px;
    padding:10px 14px; border-radius:999px; z-index:15; }
  .zr-cu-tray.open { display:flex; }
  .zr-cu-tray button { font-size:24px; background:transparent; border:0; cursor:pointer; }
  .zr-cu-floats { position:absolute; inset:0; pointer-events:none; overflow:hidden; z-index:16; }
  .zr-cu-float { position:absolute; bottom:90px; font-size:40px; transition: all 2.4s ease-out; }
  /* People panel */
  .zr-cu-people { position:absolute; top:12px; right:12px; width:min(280px,80vw); max-height:60%; overflow:auto;
    padding:14px; border-radius:16px; z-index:14; display:none; }
  .zr-cu-people.open { display:block; }
  .zr-cu-people h4 { margin:0 0 10px; font-size:14px; }
  .zr-cu-people .row { display:flex; justify-content:space-between; gap:8px; padding:8px 0; font-size:13px;
    border-bottom:1px solid rgba(255,255,255,.08); }
  @media (max-width:640px){ .zr-cu-stage.fullscreen-pip .zr-cu-self{ width:92px; height:132px; } .zr-cu-btn{ min-width:48px; min-height:48px; } }
  `;
  document.head.append(el('style', { id: STYLE_ID }, css));
}

/**
 * Mount the native glassmorphism call UI over the page (or a container) and wire it to the
 * ZrClient. Returns a handle to unmount or switch styles at runtime.
 */
export function mountCallUI(client: CallUIClient, opts: CallUIOptions = {}): CallUIHandle {
  injectStyles();
  const mountEl = opts.mount ?? document.body;
  const glass = opts.glass !== false;

  let spec = callUxSpec({ style: opts.style, mode: opts.mode, maxParticipants: opts.maxParticipants });
  const state = { mic: true, camera: true, speaker: true, hand: false, trayOpen: false, peopleOpen: false, startedAt: 0 };
  const remoteTiles = new Map<string, { tile: HTMLElement; video: HTMLVideoElement }>();

  const root = el('div', { class: 'zr-call-ui' });
  mountEl.append(root);
  const glassClass = glass ? ' zr-glass' : '';

  // ---- header ----
  const titleName = el('b', {}, opts.displayName || (spec.style === 'whatsapp' ? 'Secure call' : 'Room'));
  const timer = el('span', { class: 'zr-cu-timer' }, spec.showCallTimer ? '0:00' : '');
  const e2eeBadge = el('span', { class: 'zr-cu-badge zr-cu-e2ee' }, '🔒 End-to-end encrypted');
  const countBadge = el('span', { class: 'zr-cu-badge zr-cu-count' }, '1 in call');
  const sub = el('span', { class: 'zr-cu-sub' });
  const header = el('div', { class: 'zr-cu-header' + glassClass },
    el('div', { class: 'zr-cu-title' }, titleName, sub));
  root.append(header);

  // ---- stage ----
  const stage = el('div', { class: 'zr-cu-stage ' + spec.layout });
  const floats = el('div', { class: 'zr-cu-floats' });
  root.append(stage);

  // whatsapp video nodes
  const remoteVideo = el('video', { autoplay: '', playsinline: '' }) as HTMLVideoElement;
  const selfVideoWa = el('video', { autoplay: '', playsinline: '', muted: '' }) as HTMLVideoElement;
  // meet grid
  const grid = el('div', { class: 'zr-cu-grid' });
  const selfTileMeet = el('div', { class: 'zr-cu-tile' },
    el('video', { autoplay: '', playsinline: '', muted: '' }),
    el('div', { class: 'zr-cu-name' }, 'You'),
    el('div', { class: 'zr-cu-hand' }, '✋'));
  const selfVideoMeet = selfTileMeet.querySelector('video') as HTMLVideoElement;

  // people panel + reaction tray
  const peoplePanel = el('div', { class: 'zr-cu-people' + glassClass }, el('h4', {}, 'In this call'));
  const tray = el('div', { class: 'zr-cu-tray' + glassClass });
  for (const emo of REACTION_EMOJIS) {
    tray.append(el('button', { type: 'button', onclick: () => { client.sendReaction?.(emo); floatReaction(emo); state.trayOpen = false; tray.classList.remove('open'); } }, emo));
  }

  // ---- control bar ----
  const bar = el('div', { class: 'zr-cu-bar' + glassClass });
  const btns = new Map<string, HTMLButtonElement>();

  function makeBtn(c: CallControlSpec): HTMLButtonElement {
    const b = el('button', { class: 'zr-cu-btn' + (c.danger ? ' danger' : ''), type: 'button', title: c.label },
      el('span', { class: 'zr-ic' }, c.icon), el('span', { class: 'zr-lb' }, c.label)) as HTMLButtonElement;
    b.addEventListener('click', () => onControl(c.id, b));
    btns.set(c.id, b);
    return b;
  }

  function renderControls() {
    bar.replaceChildren();
    btns.clear();
    for (const c of spec.controls) bar.append(makeBtn(c));
  }

  function onControl(id: string, b: HTMLButtonElement) {
    switch (id) {
      case 'mic': state.mic = !state.mic; client.setMicEnabled(state.mic); b.classList.toggle('off', !state.mic); break;
      case 'camera': state.camera = !state.camera; client.setCameraEnabled(state.camera); b.classList.toggle('off', !state.camera); bindLocal(); break;
      case 'speaker': state.speaker = !state.speaker; remoteVideo.muted = !state.speaker; remoteTiles.forEach((t) => (t.video.muted = !state.speaker)); b.classList.toggle('off', !state.speaker); break;
      case 'flip-camera': flipCamera(); break;
      case 'present': togglePresent(b); break;
      case 'reactions': state.trayOpen = !state.trayOpen; tray.classList.toggle('open', state.trayOpen); break;
      case 'raise-hand': state.hand = !state.hand; client.raiseHand?.(state.hand); b.classList.toggle('on-accent', state.hand); selfTileMeet.classList.toggle('raised', state.hand); break;
      case 'participants': state.peopleOpen = !state.peopleOpen; peoplePanel.classList.toggle('open', state.peopleOpen); break;
      case 'end': endCall(); break;
    }
  }

  async function togglePresent(b: HTMLButtonElement) {
    try {
      if (!client.isScreenSharing) { await client.startScreenShare?.({ selfOverlay: true }); b.classList.add('on-accent'); }
      else { await client.stopScreenShare?.(); b.classList.remove('on-accent'); }
      bindLocal();
    } catch { /* user cancelled the picker */ }
  }

  async function flipCamera() {
    // Best-effort facingMode flip; the app can override by re-supplying a stream.
    try {
      const cur = client.localStream?.getVideoTracks?.()[0] as any;
      const facing = cur?.getSettings?.().facingMode === 'environment' ? 'user' : 'environment';
      const next = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      const track = next.getVideoTracks()[0];
      await (client as any).replaceVideoTrack?.(track);
      bindLocal();
    } catch { /* single-camera device */ }
  }

  async function endCall() {
    try { await (client.hangup ? client.hangup() : client.leave?.()); } catch { /* ignore */ }
    handle.unmount();
  }

  // ---- incoming overlay ----
  const incoming = el('div', { class: 'zr-cu-incoming' + glassClass });
  function renderIncoming(from: string, name?: string) {
    incoming.replaceChildren();
    const bar2 = el('div', { class: 'zr-cu-bar' });
    bar2.append(
      el('button', { class: 'zr-cu-btn danger', type: 'button', onclick: async () => { await client.decline?.(); handle.unmount(); } },
        el('span', { class: 'zr-ic' }, '📵'), el('span', { class: 'zr-lb' }, 'Decline')),
      el('button', { class: 'zr-cu-btn accept', type: 'button', onclick: async () => { incoming.remove(); await client.accept?.(); } },
        el('span', { class: 'zr-ic' }, '📞'), el('span', { class: 'zr-lb' }, 'Accept')),
    );
    incoming.append(
      el('div', { class: 'zr-cu-avatar' }, (name || from || '?')[0].toUpperCase()),
      el('h2', {}, name || from),
      el('p', {}, spec.style === 'whatsapp' ? '🔒 Incoming secure call…' : 'Incoming call…'),
      bar2,
    );
    stage.append(incoming);
  }

  // ---- video binding ----
  function layoutStage() {
    stage.className = 'zr-cu-stage ' + spec.layout;
    stage.replaceChildren();
    if (spec.layout === 'fullscreen-pip') {
      stage.append(el('div', { class: 'zr-cu-remote' }, remoteVideo), el('div', { class: 'zr-cu-self' }, selfVideoWa));
    } else {
      grid.replaceChildren(selfTileMeet);
      for (const { tile } of remoteTiles.values()) grid.append(tile);
      stage.append(grid);
    }
    stage.append(floats, tray, peoplePanel);
    bindLocal();
  }

  function bindLocal() {
    const s = client.displayStream || client.localStream;
    if (spec.layout === 'fullscreen-pip') { if (selfVideoWa.srcObject !== s && s) selfVideoWa.srcObject = s; }
    else if (selfVideoMeet.srcObject !== s && s) selfVideoMeet.srcObject = s;
  }

  function upsertRemote(id: string, stream?: MediaStream) {
    if (spec.layout === 'fullscreen-pip') { if (stream && remoteVideo.srcObject !== stream) remoteVideo.srcObject = stream; return; }
    let entry = remoteTiles.get(id);
    if (!entry) {
      const video = el('video', { autoplay: '', playsinline: '' }) as HTMLVideoElement;
      const tile = el('div', { class: 'zr-cu-tile' }, video, el('div', { class: 'zr-cu-name' }, id), el('div', { class: 'zr-cu-hand' }, '✋'));
      entry = { tile, video };
      remoteTiles.set(id, entry);
      grid.append(tile);
    }
    if (stream && entry.video.srcObject !== stream) entry.video.srcObject = stream;
    updateCount();
  }

  function removeRemote(id: string) {
    const e = remoteTiles.get(id);
    if (e) { e.tile.remove(); remoteTiles.delete(id); }
    updateCount();
  }

  function updateCount() {
    countBadge.textContent = `${remoteTiles.size + 1} in call`;
  }

  function renderPeople() {
    peoplePanel.replaceChildren(el('h4', {}, `In this call · ${remoteTiles.size + 1}`));
    const raised = new Set((client.raisedHands || []).map((h) => h.peerId));
    peoplePanel.append(el('div', { class: 'row' }, el('span', {}, 'You'), el('span', {}, state.hand ? '✋' : '')));
    for (const id of remoteTiles.keys()) peoplePanel.append(el('div', { class: 'row' }, el('span', {}, id), el('span', {}, raised.has(id) ? '✋' : '')));
  }

  function floatReaction(emoji: string) {
    const f = el('div', { class: 'zr-cu-float', style: `left:${20 + Math.random() * 60}%` }, emoji);
    floats.append(f);
    requestAnimationFrame(() => { f.style.bottom = '70%'; f.style.opacity = '0'; });
    setTimeout(() => f.remove(), 2500);
  }

  // ---- header composition per style ----
  function renderHeader() {
    sub.replaceChildren();
    if (spec.showCallTimer) sub.append(timer);
    if (spec.showE2eeBanner && client.e2eeActive !== false) sub.append(e2eeBadge);
    if (spec.style === 'google-meet') sub.append(countBadge);
  }

  // ---- event wiring ----
  let tick: any;
  function startTimer() {
    if (tick || !spec.showCallTimer) return;
    state.startedAt = Date.now();
    tick = setInterval(() => { timer.textContent = formatCallDuration((Date.now() - state.startedAt) / 1000); }, 1000);
  }

  const unsub = client.events.on((e: any) => {
    switch (e.type) {
      case 'track-subscribed': if (e.track?.kind === 'video' && e.track.stream) upsertRemote(e.participantId, e.track.stream); break;
      case 'participant-joined': upsertRemote(e.participant.id, client.getParticipantStream?.(e.participant.id)); break;
      case 'participant-left': removeRemote(e.participantId); break;
      case 'call-phase-changed':
        if (e.phase === 'active' || e.phase === 'connected') { incoming.remove(); startTimer(); }
        if (e.phase === 'ended') handle.unmount();
        break;
      case 'incoming-call': renderIncoming(e.from, e.displayName || opts.displayName); break;
      case 'reaction-received': floatReaction(e.emoji); break;
      case 'hand-raised-changed': {
        const t = remoteTiles.get(e.participantId); if (t) t.tile.classList.toggle('raised', e.raised);
        if (state.peopleOpen) renderPeople();
        break;
      }
      case 'screen-share-changed': bindLocal(); break;
    }
  });

  // ---- first render ----
  function render() {
    renderHeader();
    renderControls();
    layoutStage();
    if (client.callPhase === 'active' || client.callPhase === 'connected') startTimer();
    if (client.callPhase === 'ringing' && client.incomingCall) renderIncoming(client.incomingCall.from, client.incomingCall.displayName || opts.displayName);
  }

  root.replaceChildren(header, stage, bar);
  render();
  // keep the people panel fresh when opened
  peoplePanel.addEventListener('click', () => { if (state.peopleOpen) renderPeople(); });

  const handle: CallUIHandle = {
    root,
    unmount() {
      try { unsub(); } catch { /* ignore */ }
      if (tick) clearInterval(tick);
      root.remove();
    },
    setStyle(style: CallUxChoice) {
      spec = callUxSpec({ style, mode: opts.mode, maxParticipants: opts.maxParticipants });
      // remote tiles are recreated on next track events; re-render the shell.
      remoteTiles.clear();
      render();
    },
  };
  return handle;
}
